import { useState, useEffect } from 'react';
import api from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// Config par plateforme : préfixe (+ suffixe pour le cas sous-domaine Bandcamp).
// L'utilisateur ne saisit que son identifiant ; l'URL complète est reconstruite.
const SOCIAL_PLATFORMS_CONFIG = {
  Instagram: { prefix: 'https://instagram.com/', suffix: '' },
  SoundCloud: { prefix: 'https://soundcloud.com/', suffix: '' },
  YouTube: { prefix: 'https://youtube.com/@', suffix: '' },
  TikTok: { prefix: 'https://tiktok.com/@', suffix: '' },
  Facebook: { prefix: 'https://facebook.com/', suffix: '' },
  Mixcloud: { prefix: 'https://mixcloud.com/', suffix: '' },
  Spotify: { prefix: 'https://open.spotify.com/artist/', suffix: '' },
  Bandcamp: { prefix: 'https://', suffix: '.bandcamp.com' },
};

const SOCIAL_PLATFORMS = Object.keys(SOCIAL_PLATFORMS_CONFIG);

// Lookup nom de plateforme depuis la clé stockée en base (minuscule).
const PLATFORM_BY_LOWER = Object.fromEntries(
  SOCIAL_PLATFORMS.map((p) => [p.toLowerCase(), p])
);

/**
 * Nettoie un identifiant saisi : espaces, '@' ou '/' en tête, '/' en fin.
 */
function cleanHandle(raw) {
  return (raw || '').trim().replace(/^[@/]+/, '').replace(/\/+$/, '');
}

/**
 * Reconstruit l'URL complète depuis un handle. Plateforme inconnue → handle tel quel.
 */
function buildSocialUrl(platform, handle) {
  const cfg = SOCIAL_PLATFORMS_CONFIG[platform];
  const clean = cleanHandle(handle);
  return cfg ? `${cfg.prefix}${clean}${cfg.suffix}` : clean;
}

/**
 * Extrait le handle d'une URL stockée en retirant préfixe/suffixe connus.
 * Si l'URL ne matche pas le format attendu, on la garde telle quelle (fallback).
 */
function extractHandle(platform, url) {
  const cfg = SOCIAL_PLATFORMS_CONFIG[platform];
  if (!cfg || !url) return url || '';
  let h = url;
  if (cfg.prefix && h.startsWith(cfg.prefix)) h = h.slice(cfg.prefix.length);
  else return url;
  if (cfg.suffix) {
    if (h.endsWith(cfg.suffix)) h = h.slice(0, -cfg.suffix.length);
    else return url;
  }
  return h;
}

/**
 * Convert API response format { instagram: 'url', soundcloud: 'url' } to form format
 * [{ platform, handle }]
 */
function convertSocialLinksObjectToArray(obj) {
  return Object.entries(obj || {}).map(([key, url]) => {
    const platform = PLATFORM_BY_LOWER[key.toLowerCase()] || key;
    return { platform, handle: extractHandle(platform, url) };
  });
}

/**
 * Convert form format [{ platform, handle }] to API format { instagram: 'url', ... }
 * Le backend continue de stocker des URLs complètes.
 */
function convertSocialLinksArrayToObject(array) {
  const obj = {};
  array.forEach(({ platform, handle }) => {
    const clean = cleanHandle(handle);
    if (platform && clean) {
      obj[platform.toLowerCase()] = buildSocialUrl(platform, clean);
    }
  });
  return obj;
}

export default function ArtistProfileForm({ initialData, onSuccess, mode = 'create' }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    artistName: '',
    bio: '',
    genres: [],
    primaryGenreId: null,
    city: '',
    country: 'France',
    socialLinks: [],
  });

  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [availableGenres, setAvailableGenres] = useState([]);

  // Fetch available genres from API
  useEffect(() => {
    async function fetchGenres() {
      try {
        const res = await api.get('/artists/genres');
        setAvailableGenres(res.data.genres || []);
      } catch {
        // Fallback silencieux — les genres ne seront pas affichés
      }
    }
    fetchGenres();
  }, []);

  // Pré-remplir le nom d'artiste avec le nom d'affichage (création uniquement,
  // sans écraser une saisie en cours).
  useEffect(() => {
    if (mode === 'create' && user?.displayName) {
      setFormData((prev) => (prev.artistName ? prev : { ...prev, artistName: user.displayName }));
    }
  }, [mode, user?.displayName]);

  // Initialize form with existing data in edit mode
  useEffect(() => {
    if (initialData && mode === 'edit') {
      const genreIds = (initialData.genres || []).map((g) => g.id);
      const primary = (initialData.genres || []).find((g) => g.isPrimary);
      setFormData({
        artistName: initialData.artistName || '',
        bio: initialData.bio || '',
        // initialData.genres = [{ id, name, slug, isPrimary }] → on stocke les IDs
        genres: genreIds,
        // on préserve le genre principal sauvegardé (fallback : premier de la liste)
        primaryGenreId: primary ? primary.id : (genreIds[0] ?? null),
        // city/country sont imbriqués dans location dans la réponse API
        city: initialData.location?.city || initialData.city || '',
        country: initialData.location?.country || initialData.country || 'France',
        socialLinks: convertSocialLinksObjectToArray(initialData.socialLinks || {}),
      });
      setProfilePhotoUrl(initialData.profilePhotoUrl || null);
    }
  }, [initialData, mode]);

  function validateForm() {
    const newErrors = {};

    if (!formData.artistName.trim()) {
      newErrors.artistName = 'Le nom d\'artiste est requis';
    } else if (formData.artistName.length > 100) {
      newErrors.artistName = 'Le nom doit faire 100 caractères maximum';
    }

    if (formData.bio && formData.bio.length > 2000) {
      newErrors.bio = 'La bio doit faire 2000 caractères maximum';
    }

    if (formData.genres.length === 0) {
      newErrors.genres = 'Sélectionnez au moins un genre';
    } else if (formData.genres.length > 5) {
      newErrors.genres = '5 genres maximum';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        artistName: formData.artistName,
        bio: formData.bio,
        // Le backend attend [{ genreId, isPrimary? }] — le principal est choisi explicitement
        // (fallback sur le premier sélectionné si aucun principal valide)
        genres: formData.genres.map((id) => ({
          genreId: id,
          isPrimary:
            id ===
            (formData.genres.includes(formData.primaryGenreId)
              ? formData.primaryGenreId
              : formData.genres[0]),
        })),
        city: formData.city,
        country: formData.country,
        socialLinks: convertSocialLinksArrayToObject(formData.socialLinks),
      };

      if (mode === 'create') {
        await api.post('/artists/profile', payload);
      } else {
        await api.put('/artists/profile', payload);
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde du profil');
    } finally {
      setLoading(false);
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  }

  function toggleGenre(genreId) {
    setFormData((prev) => {
      const isSelected = prev.genres.includes(genreId);
      let genres;
      let primaryGenreId = prev.primaryGenreId;
      if (isSelected) {
        genres = prev.genres.filter((id) => id !== genreId);
        // si on désélectionne le principal, on bascule sur le premier restant
        if (primaryGenreId === genreId) {
          primaryGenreId = genres[0] ?? null;
        }
      } else {
        genres = [...prev.genres, genreId];
        // le premier genre sélectionné devient principal par défaut
        if (primaryGenreId == null) {
          primaryGenreId = genreId;
        }
      }
      return { ...prev, genres, primaryGenreId };
    });
    if (fieldErrors.genres) {
      setFieldErrors((prev) => ({
        ...prev,
        genres: null,
      }));
    }
  }

  function setPrimaryGenre(genreId) {
    setFormData((prev) =>
      prev.genres.includes(genreId) ? { ...prev, primaryGenreId: genreId } : prev
    );
  }

  function addSocialLink() {
    setFormData((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { platform: '', handle: '' }],
    }));
  }

  function removeSocialLink(index) {
    setFormData((prev) => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index),
    }));
  }

  function handleSocialLinkChange(index, field, value) {
    setFormData((prev) => {
      const newLinks = [...prev.socialLinks];
      newLinks[index] = {
        ...newLinks[index],
        [field]: value,
      };
      return {
        ...prev,
        socialLinks: newLinks,
      };
    });
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const formDataForUpload = new FormData();
      formDataForUpload.append('photo', file);

      const response = await api.upload('/artists/profile/photo', formDataForUpload);
      setProfilePhotoUrl(response.data?.profilePhotoUrl);
    } catch {
      setError('Erreur lors de l\'envoi de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border th-border p-6 space-y-6 th-bg-surface transition-colors duration-300">
      {/* Error Banner */}
      {error && (
        <div className="border border-status-rejected/30 text-status-rejected px-3 py-2 text-sm font-mono">
          {error}
        </div>
      )}

      {/* Artist Name */}
      <div>
        <label htmlFor="artistName" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Nom d'artiste
        </label>
        <input
          type="text"
          id="artistName"
          name="artistName"
          value={formData.artistName}
          onChange={handleInputChange}
          maxLength={100}
          className="th-input"
          placeholder="Votre nom d'artiste ou de DJ"
        />
        {fieldErrors.artistName && (
          <p className="text-status-rejected text-xs mt-1 font-mono">{fieldErrors.artistName}</p>
        )}
      </div>

      {/* Bio */}
      <div>
        <label htmlFor="bio" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Bio
        </label>
        <textarea
          id="bio"
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          maxLength={2000}
          rows={4}
          className="th-input resize-none"
          placeholder="Parlez-nous de vous... (max 2000 car.)"
        />
        <p className="th-text-muted text-xs mt-1 font-mono">
          {formData.bio.length} / 2000
        </p>
        {fieldErrors.bio && (
          <p className="text-status-rejected text-xs mt-1 font-mono">{fieldErrors.bio}</p>
        )}
      </div>

      {/* Genres */}
      <div>
        <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Genres (5 max)
        </label>
        {availableGenres.length === 0 ? (
          <p className="th-text-muted text-sm">Chargement des genres...</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {availableGenres.map((genre) => {
                const selected = formData.genres.includes(genre.id);
                const isPrimary = selected && formData.primaryGenreId === genre.id;
                return (
                  <span
                    key={genre.id}
                    className={
                      selected
                        ? `inline-flex items-center gap-1.5 border px-3 py-1 text-xs font-mono uppercase tracking-widest transition-colors duration-150 ${isPrimary ? 'border-accent bg-accent/10 text-accent' : 'border-accent/50 th-text'}`
                        : 'inline-flex items-center border th-border th-text-secondary px-3 py-1 text-xs font-mono uppercase tracking-widest transition-colors duration-150 hover:border-accent hover:text-accent'
                    }
                  >
                    <button
                      type="button"
                      onClick={() => toggleGenre(genre.id)}
                      className="cursor-pointer"
                    >
                      {genre.name}
                    </button>
                    {selected && (
                      <button
                        type="button"
                        onClick={() => setPrimaryGenre(genre.id)}
                        aria-label={isPrimary ? `${genre.name} est le genre principal` : `Définir ${genre.name} comme genre principal`}
                        title={isPrimary ? 'Genre principal' : 'Définir comme genre principal'}
                        className={`leading-none text-sm cursor-pointer transition-colors duration-150 ${isPrimary ? 'text-accent' : 'th-text-muted hover:text-accent'}`}
                      >
                        {isPrimary ? '★' : '☆'}
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
            <p className="th-text-muted text-xs mt-2 font-mono">
              Cliquez sur l'étoile d'un genre sélectionné pour le définir comme principal (mis en avant sur votre profil).
            </p>
          </>
        )}
        {fieldErrors.genres && (
          <p className="text-status-rejected text-xs mt-1 font-mono">{fieldErrors.genres}</p>
        )}
      </div>

      {/* City */}
      <div>
        <label htmlFor="city" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Ville
        </label>
        <input
          type="text"
          id="city"
          name="city"
          value={formData.city}
          onChange={handleInputChange}
          className="th-input"
          placeholder="ex : Paris"
        />
      </div>

      {/* Country */}
      <div>
        <label htmlFor="country" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Pays
        </label>
        <input
          type="text"
          id="country"
          name="country"
          value={formData.country}
          onChange={handleInputChange}
          className="th-input"
          placeholder="ex : France"
        />
      </div>

      {/* Social Links */}
      <div>
        <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Liens sociaux
        </label>
        <div className="space-y-3">
          {formData.socialLinks.map((link, index) => {
            const cfg = SOCIAL_PLATFORMS_CONFIG[link.platform];
            return (
              <div key={index} className="flex flex-wrap gap-2">
                <select
                  value={link.platform}
                  onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                  className="bg-transparent border th-border th-text text-sm focus:border-accent outline-none transition-colors duration-150 px-3 py-2.5"
                >
                  <option value="">Choisir une plateforme</option>
                  {SOCIAL_PLATFORMS.map((platform) => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </select>
                <div className="flex flex-1 items-stretch min-w-[200px]">
                  {cfg?.prefix && (
                    <span className="flex items-center px-2 text-xs font-mono th-text-muted border border-r-0 th-border whitespace-nowrap">
                      {cfg.prefix}
                    </span>
                  )}
                  <input
                    type="text"
                    value={link.handle}
                    onChange={(e) => handleSocialLinkChange(index, 'handle', e.target.value)}
                    disabled={!link.platform}
                    className="th-input flex-1 min-w-0 disabled:opacity-50"
                    placeholder={link.platform ? 'votre-identifiant' : 'Choisissez une plateforme'}
                  />
                  {cfg?.suffix && (
                    <span className="flex items-center px-2 text-xs font-mono th-text-muted border border-l-0 th-border whitespace-nowrap">
                      {cfg.suffix}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeSocialLink(index)}
                  className="th-btn"
                >
                  Supprimer
                </button>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addSocialLink}
          className="mt-2 th-btn"
        >
          + Ajouter un lien
        </button>
      </div>

      {/* Photo Upload */}
      <div>
        <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Photo de profil
        </label>
        {profilePhotoUrl && (
          <div className="mb-3">
            <img
              src={profilePhotoUrl}
              alt="Profile preview"
              className="w-32 h-32 object-cover border th-border"
            />
          </div>
        )}
        <input
          type="file"
          id="photoInput"
          accept="image/*"
          onChange={handlePhotoUpload}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => document.getElementById('photoInput').click()}
          disabled={uploadingPhoto}
          className="th-btn-accent disabled:opacity-50"
        >
          {uploadingPhoto ? 'Envoi...' : 'Charger une photo'}
        </button>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full th-btn-accent disabled:opacity-50"
      >
        {loading ? 'Enregistrement...' : mode === 'create' ? 'Créer le profil' : 'Enregistrer'}
      </button>
    </form>
  );
}
